import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  AppBar,
  Box,
  Toolbar,
} from "@mui/material";
import { Search, Folder, Add, MoreVert } from "@mui/icons-material";
import { onAuthStateChanged, getAuth, signOut } from "firebase/auth";
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate } from "react-router-dom";
import LogoImage from "../statics/logo.svg";
import Swal from "sweetalert2";

const PAGE_SIZE = 8;

// Mock loading animation component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
  </div>
);

// Simple date formatter (replacing dayjs)
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadge = (status) => {
  const statusConfig = {
    draft: { color: "default", label: "Draft" },
    prepared: { color: "#FF9800", label: "Prepared" },
    publish: { color: "#4BAD8E", label: "Publish" },
    unpublished: { color: "#E94C4C", label: "Unpublished" },
  };

  const config = statusConfig[status] || { color: "default", label: "Unknown" };
  return (
    <Chip
      label={config.label}
      size="small"
      sx={
        config.color.startsWith("#")
          ? { backgroundColor: config.color, color: "white" }
          : { color: config.color }
      }
    />
  );
};

const EventCard = ({
  event,
  statusBadge,
  onPreview,
  onStatusUpdate,
  onDelete,
  onClick,
}) => {
  const [imageSrc, setImageSrc] = useState(event.cover);
  const [hasImageError, setHasImageError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleImageError = () => {
    if (!hasImageError) {
      setImageSrc(
        "https://placehold.co/400x200/EEEEEE/D0D0D0?text=%20Not%20Found"
      );
      setHasImageError(true);
    }
  };

  const handleMenuClick = (event) => {
    event.stopPropagation(); // Prevent card click when opening menu
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    // event can be undefined when called from Menu onClose
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Helper to format unix time to DDMMYYYY
  const formatUnixToDDMMYYYY = (unixTime) => {
    if (!unixTime) return "";
    const date = new Date(Number(unixTime));
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}${mm}${yyyy}`;
  };

  const isPublished = event.status === "publish";

  return (
    <Card
      sx={{
        maxWidth: 345,
        transition: "transform 0.2s",
        "&:hover": { transform: "scale(1.05)", cursor: "pointer" },
      }}
      onClick={onClick}
    >
      <div style={{ position: "relative" }} onClick={onClick}>
        <CardMedia
          component="img"
          height="200"
          image={imageSrc}
          alt={event.title}
          onError={handleImageError}
          sx={{ aspectRatio: "2/1", objectFit: "cover" }}
        />
        <div style={{ position: "absolute", top: 12, right: 12 }}>
          {statusBadge(event.status)}
        </div>
      </div>
      <CardContent sx={{ textAlign: "left" }}>
        <div style={{ display: "flex", alignItems: "start", marginBottom: 8 }}>
          <Folder sx={{ color: "#8A5918", marginRight: 1 }} />
          <Typography
            variant="subtitle2"
            sx={{ color: "#8A5918", fontWeight: "bold", flexGrow: 1 }}
          >
            {event.eventDate
              ? `${formatUnixToDDMMYYYY(event.eventDate)} - ${event.title}`
              : event.title}
          </Typography>
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            sx={{ zIndex: 1000 }}
          >
            {isPublished && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClose(e);
                  window.open(`${process.env.REACT_APP_SCHEMA_DOMAIN?.replace(/\/$/, "") || ""}/${event.id}`, '_blank');
                }}
              >
                View Live Site
              </MenuItem>
            )}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose(e);
                window.open(`/public/activity/${event.id}`, '_blank');
              }}
            >
              Public Activity
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose(e);
                onPreview && onPreview();
              }}
            >
              Preview
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose(e);
                onStatusUpdate && onStatusUpdate(isPublished ? "unpublished" : "publish");
              }}
            >
              {isPublished ? "Unpublish" : "Publish"}
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose(e);
                onDelete && onDelete();
              }}
              sx={{ color: "error.main" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </div>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ marginBottom: 2, textAlign: "left" }}
        >
          {event.desc}
        </Typography>
        <div style={{ borderTop: "1px solid #f4e6d0", paddingTop: 8 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ textAlign: "left" }}
          >
            <strong>Created Date:</strong> {formatDate(event.created)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ textAlign: "left" }}
          >
            <strong>Last Updated:</strong> {formatDate(event.updated)}
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};

const EventCardSkeleton = () => (
  <Card sx={{ maxWidth: 345 }}>
    <div
      style={{
        height: 200,
        backgroundColor: "#f0f0f0",
        animation: "pulse 2s infinite",
      }}
    />
    <CardContent>
      <div
        style={{
          height: 20,
          backgroundColor: "#f0f0f0",
          marginBottom: 8,
          animation: "pulse 2s infinite",
        }}
      />
      <div
        style={{
          height: 16,
          backgroundColor: "#f0f0f0",
          marginBottom: 8,
          animation: "pulse 2s infinite",
        }}
      />
      <div
        style={{
          height: 16,
          backgroundColor: "#f0f0f0",
          width: "60%",
          animation: "pulse 2s infinite",
        }}
      />
    </CardContent>
  </Card>
);

const PREVIEW_BASE_URL =
  process.env.REACT_APP_SCHEMA_DOMAIN?.replace(/\/$/, "") || "";

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const loadMoreRef = useRef(null);
  const navigate = useNavigate();

  // Load events from Firestore
  const loadEventsFromFirestore = async (search = "") => {
    try {
      // setLoadingFirst(true);
      const eventsRef = collection(db, "events");
      // Remove orderBy to ensure we get ALL events, even those without createdAt
      // let q = query(eventsRef, orderBy("createdAt", "desc"));
      let q = query(eventsRef);

      const querySnapshot = await getDocs(q);
      const firestoreEvents = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.information?.eventName || "Untitled Event",
          desc: data.information?.description || "No description available",
          status: data.information?.eventStatus || "draft",
          cover: data.main?.backgroundImage || "/wedding.jpg",
          eventDate: data.information?.eventDate || null,
          created:
            data.createdAt?.toDate?.()?.toISOString() ||
            new Date().toISOString(),
          updated:
            data.updatedAt?.toDate?.()?.toISOString() ||
            new Date().toISOString(),
        };
      }).sort((a, b) => new Date(b.created) - new Date(a.created));

      // Filter by search term if provided
      const filteredEvents = search
        ? firestoreEvents.filter((event) =>
          event.title.toLowerCase().includes(search.toLowerCase())
        )
        : firestoreEvents;

      setAllEvents(filteredEvents);
      setEvents(filteredEvents.slice(0, PAGE_SIZE));
      setLoadingFirst(false);
    } catch (error) {
      console.error("Error loading events:", error);
      setLoadingFirst(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (eventId, newStatus) => {
    try {
      const eventRef = doc(db, "events", eventId);
      await updateDoc(eventRef, {
        "information.eventStatus": newStatus,
        updatedAt: new Date(),
      });

      // Update local state
      const updateEvents = (eventList) =>
        eventList.map(ev => ev.id === eventId ? { ...ev, status: newStatus } : ev);

      setAllEvents(prev => updateEvents(prev));
      setEvents(prev => updateEvents(prev));

      Swal.fire({
        icon: "success",
        title: "Status Updated",
        text: `Event status changed to ${newStatus}`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update event status",
      });
    }
  };

  // Handle delete
  const handleDelete = async (eventId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, "events", eventId));

          // Update local state
          const filterEvents = (eventList) => eventList.filter(ev => ev.id !== eventId);
          setAllEvents(prev => filterEvents(prev));
          setEvents(prev => filterEvents(prev));

          Swal.fire("Deleted!", "Your event has been deleted.", "success");
        } catch (error) {
          console.error("Error deleting event:", error);
          Swal.fire("Error!", "Failed to delete event.", "error");
        }
      }
    });
  };

  // Handle search
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Debounce search
    const timer = setTimeout(() => {
      loadEventsFromFirestore(value);
      setPage(1);
    }, 1500);

    return () => clearTimeout(timer);
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Error getting ID token:", error);
        }
      } else {
        setIsAuthenticated(false);
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Only load events if authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    loadEventsFromFirestore();
  }, [isAuthenticated]);

  useEffect(() => {
    if (loading) return;
    if (events.length >= allEvents.length) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          setLoading(true);
          setTimeout(() => {
            setEvents((prev) =>
              allEvents.slice(
                0,
                Math.min(prev.length + PAGE_SIZE, allEvents.length)
              )
            );
            setPage((p) => p + 1);
            setLoading(false);
          }, 1000);
        }
      },
      { threshold: 1 }
    );

    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => {
      observer.disconnect();
      setLoading(false);
    };
  }, [events, loading, allEvents.length]);

  // Don't render dashboard if not authenticated
  if (!isAuthenticated) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  const logout = () => {
    const auth = getAuth();
    localStorage.removeItem("authToken");
    signOut(auth)
      .then(() => {
        setIsAuthenticated(false);
        navigate("/login");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  const handlePreview = (eventId) => {
    if (!eventId) return;
    const baseUrl = PREVIEW_BASE_URL || window.location.origin;
    const previewUrl = `${baseUrl}/${eventId}/preview`;
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      style={{ minHeight: "100vh", backgroundColor: "#FAFBFD", width: "100%" }}
    >
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          width: "100%",
        }}
      >
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <img
              src={LogoImage}
              alt="SmileBurst Logo"
              style={{ height: 40, marginRight: 10 }}
              onError={(e) => {
                e.target.src =
                  "https://placehold.co/120x40/EEEEEE/8A5918?text=SmileBurst";
                e.target.onerror = null;
              }}
            />
          </Box>

          <TextField
            placeholder="Search events by name..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <Search sx={{ marginRight: 1, color: "#1C1B1F" }} />
              ),
            }}
            sx={{ width: "100%", maxWidth: 400, mx: 2 }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            <Typography sx={{ color: "#8A5918", fontWeight: "medium", mr: 2 }}>
              Hi, Admin
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#E94C4C",
                "&:hover": { backgroundColor: "#d43f3f" },
              }}
              onClick={logout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} sx={{ padding: 4, width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 30,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "#8A4B09", fontWeight: "bold" }}
          >
            All Events
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/event")}
            sx={{
              backgroundColor: "#FFC400",
              color: "#8A5918",
              "&:hover": { backgroundColor: "#e6b000" },
            }}
          >
            New Event
          </Button>
        </div>

        {/* Events grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "32px",
            width: "100%",
          }}
        >
          {loadingFirst
            ? Array.from({ length: 8 }).map((_, i) => (
              <EventCardSkeleton key={i} />
            ))
            : events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                statusBadge={statusBadge}
                onPreview={() => handlePreview(event.id)}
                onStatusUpdate={(newStatus) => handleStatusUpdate(event.id, newStatus)}
                onDelete={() => handleDelete(event.id)}
                onClick={() => navigate(`/event/${event.id}`)}
              />
            ))}
        </div>

        {events.length < allEvents.length && (
          <div
            ref={loadMoreRef}
            style={{
              height: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 32,
            }}
          >
            <LoadingSpinner />
          </div>
        )}
      </Container>
    </div>
  );
};

export default Dashboard;
